// src/components/portfolio/PortfolioTree.js
import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Plus, Folder, Scale } from 'lucide-react';
import { StockLogo } from '../../utils/stockLogo';
import PortfolioVisualization from './PortfolioVisualization';

const AllocationInput = ({ value, onChange, path }) => {
  const [localValue, setLocalValue] = useState(value?.toString() || '0');

  React.useEffect(() => {
    setLocalValue(value?.toString() || '0');
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    
    const parsed = parseFloat(newValue);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
      // Sadece valid sayı ise parent'a bildir, ama "." kontrolü yapma
      // böylece kullanıcı "5." yazabilir
      if (newValue !== '' && newValue !== '.') {
         onChange?.(path, parsed);
      }
    }
  };

  return (
    <div className="flex items-center space-x-1">
      <input
        type="number"
        value={localValue}
        onChange={handleChange}
        className="w-16 px-2 py-1 text-right border rounded
          dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100
          focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          hover:border-gray-300 dark:hover:border-gray-600"
        onClick={(e) => e.stopPropagation()}
        min="0"
        max="100"
        step="0.01"
      />
      <span className="text-gray-600 dark:text-gray-400">%</span>
    </div>
  );
};

const TreeItem = ({ 
  label, 
  level = 0, 
  allocation = 0, 
  isOpen, 
  hasChildren,
  symbol,
  symbolInfo,
  onToggle,
  onAddAsset,
  onAddCategory,
  onDelete,
  onAllocationChange,
  onDistributeEqually,
  path,
  totalChildrenAllocation
}) => {
  const renderIcon = () => {
    if (hasChildren) {
      return isOpen ?  
        <ChevronDown className="w-5 h-5 text-gray-400 dark:text-gray-500" /> :
        <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />;
    }
    if (symbol && symbol.endsWith('CURRENCY')) {
      const currencyCode = symbol.split('.')[0].toLowerCase();
      return (
        <img 
          src={`/images/currencys/${currencyCode}.png`} 
          alt={`${currencyCode} logo`} 
          className="w-5 h-5" 
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = '/images/currencys/default.png';
          }}
        />
      );
    }
    return symbol ? null : <Folder className="w-5 h-5 text-gray-400 dark:text-gray-500" />;
  };

  const getLevelStyles = () => {
    if (level === 0) {
      return 'bg-white dark:bg-[#151934] dark:hover:bg-gray-800/50 py-3 font-medium';
    }
    if (level === 1) {
      return 'bg-gray-50/50 dark:bg-gray-800/30 dark:hover:bg-gray-800/50 py-2.5';
    }
    return 'bg-gray-50/30 dark:bg-gray-800/20 dark:hover:bg-gray-800/40 py-2';
  };

 
  const renderContent = () => {
    if (symbol) {
      const displaySymbol = symbol.endsWith('CURRENCY') ? symbol.split('.')[0] : symbol;
  
      return (
        <div className="flex items-center gap-2 min-w-0">
          {symbolInfo && !symbol.endsWith('CURRENCY') && (
            <StockLogo symbol={symbol} symbolInfo={symbolInfo} variant="small" />
          )}
          <div className="flex items-center gap-2">
            <span className="font-bold text-gray-900 dark:text-gray-100">{displaySymbol}</span>
            <span className="text-gray-600 dark:text-gray-400">
               {symbolInfo?.description ? `• ${symbolInfo.description}` : ""}
            </span>
          </div>
        </div>
      );
    }
  
    return (
      <div className="flex items-center gap-3">
        <span className="text-gray-900 dark:text-gray-100 font-medium">{label}</span>
        {totalChildrenAllocation !== undefined && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            Math.abs(totalChildrenAllocation - 100) < 0.1 
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
          }`}>
            Toplam: %{totalChildrenAllocation.toFixed(2)}
          </span>
        )}
      </div>
    );
  };
  




  return (
    <div 
      className={`
        flex items-center w-full cursor-pointer
        ${getLevelStyles()}
        hover:bg-gray-100/50
        border-b border-gray-200 dark:border-gray-700/50
      `}
      style={{
        paddingLeft: `${level * 20 + 16}px`,
        paddingRight: '16px',
        borderLeft: level > 0 ? `2px solid ${level === 1 ? 'rgb(59, 130, 246, 0.2)' : 'rgb(229, 231, 235, 0.2)'}` : 'none'
      }}
      onClick={onToggle}
    >
      <div className="flex items-center flex-1 min-w-0">
        {renderIcon()}
        <div className="ml-2 flex-grow min-w-0">
          {renderContent()}
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <AllocationInput 
          value={allocation} 
          onChange={onAllocationChange} 
          path={path} 
        />

        <div className="flex items-center space-x-2">
          {!symbol && (
            <>
              {hasChildren && (
                 <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Bu grubun altındaki tüm öğelere hedef oran eşit olarak (%100 dağılım sağlayacak şekilde) dağıtılacak. Onaylıyor musunuz?')) {
                          onDistributeEqually?.(path);
                      }
                    }}
                    className="p-1 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded"
                    title="Eşit Dağıt (%100)"
                  >
                    <Scale className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddAsset?.(path);
                }}
                className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
                title="Asset Ekle"
              >
                <Plus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </button>
              {level < 2 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddCategory?.(path);
                  }}
                  className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded"
                  title="Alt Kategori Ekle"
                >
                  <Folder className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </button>
              )}
            </>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Bu öğeyi silmek istediğinizden emin misiniz?')) {
                onDelete?.(path);
              }
            }}
            className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
            title="Sil"
          >
            <svg className="w-4 h-4 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const PortfolioTree = ({
  data = {},
  symbolCache,
  onAddAsset,
  onAddCategory,
  onDelete,
  onAllocationChange,
  onDistributeEqually
}) => {
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [selectedPath, setSelectedPath] = useState('');

  const toggleItem = (itemKey, path, e) => {
    if (e) e.stopPropagation();
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemKey)) {
        newSet.delete(itemKey);
      } else {
        newSet.add(itemKey);
      }
      return newSet;
    });
    setSelectedPath(path);
  };

  const handleExpandAll = () => {
    const traverse = (items) => {
      if (!items) return [];
      
      return Object.keys(items).reduce((acc, key) => {
        if (key === 'subCategories' || key === 'assets') return acc;
        
        acc.push(key);
        const item = items[key];
        
        if (item.subCategories) {
          acc.push(...Object.keys(item.subCategories));
        }
        
        return acc;
      }, []);
    };
    
    const allCategories = traverse(data);
    setExpandedItems(new Set(allCategories));
    setSelectedPath('');
  };

  const renderItems = (items, parentPath = '') => {
    return Object.entries(items).map(([key, item]) => {
      const currentPath = parentPath ? `${parentPath}|${key}` : key;
      const isOpen = expandedItems.has(key);
      const hasSubCategories = item.subCategories && Object.keys(item.subCategories).length > 0;
      const hasAssets = item.assets && Object.keys(item.assets).length > 0;
      
      // Calculate total allocation of children
      let childrenTotal = 0;
      if (item.subCategories) {
        Object.values(item.subCategories).forEach(sub => childrenTotal += (sub.allocation || 0));
      }
      if (item.assets) {
        Object.values(item.assets).forEach(asset => childrenTotal += (asset.allocation || 0));
      }

      return (
        <div key={key}>
          <TreeItem
            label={item.name || key}
            path={currentPath}
            level={parentPath.split('|').length}
            allocation={item.allocation || 0}
            isOpen={isOpen}
            hasChildren={hasSubCategories || hasAssets}
            onToggle={(e) => toggleItem(key, currentPath, e)}
            onAddAsset={onAddAsset}
            onAddCategory={onAddCategory}
            onDelete={onDelete}
            onAllocationChange={onAllocationChange}
            onDistributeEqually={onDistributeEqually}
            totalChildrenAllocation={childrenTotal}
            symbol={item.symbol || null}
            symbolInfo={item.symbol ? { ...symbolCache?.get(item.symbol), description: item.description } : null}
          />
          
          {isOpen && (
            <>
              {hasSubCategories && (
                <div>
                  {Object.entries(item.subCategories).map(([subKey, subItem]) => {
                    const subPath = `${currentPath}|subCategories|${subKey}`;
                    
                    let subChildrenTotal = 0;
                    if (subItem.assets) {
                         Object.values(subItem.assets).forEach(asset => subChildrenTotal += (asset.allocation || 0));
                    }

                    return (
                      <div key={subKey}>
                        <TreeItem
                          label={subItem.name || subKey}
                          path={subPath}
                          level={parentPath.split('|').length + 1}
                          allocation={subItem.allocation || 0}
                          hasChildren={subItem.assets && Object.keys(subItem.assets).length > 0}
                          isOpen={expandedItems.has(subKey)}
                          onToggle={(e) => toggleItem(subKey, subPath, e)}
                          onAddAsset={onAddAsset}
                          onAddCategory={onAddCategory}
                          onDelete={onDelete}
                          onAllocationChange={onAllocationChange}
                          onDistributeEqually={onDistributeEqually}
                          totalChildrenAllocation={subChildrenTotal}
                          symbol={subItem.symbol || null}
                          symbolInfo={subItem.symbol ? { description: subItem.description } : null}
                        />
                        {expandedItems.has(subKey) && subItem.assets && (
                          <div>
                            {Object.entries(subItem.assets).map(([assetKey, assetData]) => {
                              const assetPath = `${subPath}|assets|${assetKey}`;
                              return (
                                <TreeItem
                                  key={assetKey}
                                  symbol={assetKey}
                                  path={assetPath}
                                  level={parentPath.split('|').length + 2}
                                  allocation={assetData.allocation || 0}
                                  symbolInfo={{ ...symbolCache?.get(assetKey), description: assetData.description }}
                                  onDelete={onDelete}
                                  onAllocationChange={onAllocationChange}
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {hasAssets && (
                <div>
                  {Object.entries(item.assets).map(([assetKey, assetData]) => {
                    const assetPath = `${currentPath}|assets|${assetKey}`;
                    return (
                      <TreeItem
                        key={assetKey}
                        symbol={assetKey}
                        path={assetPath}
                        level={parentPath.split('|').length + 1}
                        allocation={assetData.allocation || 0}
                        symbolInfo={{ ...symbolCache?.get(assetKey), description: assetData.description }}
                        onDelete={onDelete}
                        onAllocationChange={onAllocationChange}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      );
    });
  };

  return (
    <div className="border rounded-lg overflow-hidden dark:border-gray-700">
      <div className="p-4 bg-gray-50 dark:bg-gray-800/50 flex justify-between space-x-2">
        <div className="flex gap-2">
          <button
            onClick={handleExpandAll}
            className="px-3 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 
                      rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 
                      text-gray-900 dark:text-gray-100 flex items-center space-x-1"
          >
            <span>Tümünü Genişlet</span>
          </button>
          <button
            onClick={() => {
              setExpandedItems(new Set());
              setSelectedPath('');
            }}
            className="px-3 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 
                      rounded-md hover:bg-gray-50 dark:hover:bg-gray-700
                      text-gray-900 dark:text-gray-100 flex items-center space-x-1"
          >
            <span>Tümünü Daralt</span>
          </button>
        </div>
        <button
          onClick={() => onAddCategory('')}
          className="px-3 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 
                    rounded-md hover:bg-gray-50 dark:hover:bg-gray-700
                    text-gray-900 dark:text-gray-100 flex items-center space-x-1"
        >
          <Plus className="w-4 h-4" />
          <span>Yeni Kategori</span>
        </button>
      </div>
      <div className="divide-y divide-gray-200 dark:divide-gray-700/50">
        {renderItems(data)}
      </div>
      <PortfolioVisualization 
        data={data} 
        selectedPath={selectedPath}
        onPathChange={setSelectedPath}
      />
    </div>
  );
};

export default PortfolioTree;
